import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import CoffeeCard from '@/components/coffee/CoffeeCard';
import StarRating from '@/components/coffee/StarRating';
import { Skeleton } from '@/components/ui/skeleton';
import TopContributors from '@/components/home/TopContributors';
import TodaysPickUp from '@/components/home/TodaysPickUp';
import BadgeInfo from '@/components/home/BadgeInfo';
import { Coffee, TrendingUp, Clock } from 'lucide-react';

export default function Home() {
  const [allCoffees, setAllCoffees] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [coffeeMap, setCoffeeMap] = useState({});
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.entities.Coffee.list('-average_rating'),
      api.entities.Review.list('-created_date', 10),
      // Base44 had no count API, so the total was obtained by fetching a
      // thousand rows and reading .length. This is a HEAD request.
      api.entities.Review.count(),
    ]).then(([coffees, reviews, totalReviews]) => {
      setAllCoffees(coffees);
      setAllReviews(reviews);
      setReviewCount(totalReviews);
      const map = {};
      coffees.forEach((c) => (map[c.id] = c));
      setCoffeeMap(map);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // Mobile shows 6, desktop (md+) shows 8; extra items are hidden on mobile via CSS.
  const topCoffees = useMemo(() => allCoffees.slice(0, 8), [allCoffees]);

  // Mobile shows 5, desktop (md+) shows 6; the extra item is hidden on mobile via CSS.
  const latestReviews = useMemo(
    () => allReviews.filter((r) => coffeeMap[r.coffee_id]).slice(0, 6),
    [allReviews, coffeeMap]
  );

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative bg-[#1C1209] px-5 pt-16 pb-14 md:pt-28 md:pb-20 overflow-hidden">
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg,transparent,transparent 39px,#c8a96e 39px,#c8a96e 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,#c8a96e 39px,#c8a96e 40px)',
          }}
        />
        <div className="relative max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-amber-600" />
              <span className="text-[10px] tracking-[0.25em] text-amber-600 uppercase font-medium">
                Signature Coffee, Honestly Reviewed
              </span>
            </div>
            <h1
              className="font-heading text-white font-semibold leading-[1.05] mb-5"
              style={{ fontSize: 'clamp(2.4rem, 6vw, 3.8rem)' }}
            >
              The world's<br />
              coffee,{' '}
              <em className="italic font-normal" style={{ color: '#c8a96e' }}>noted</em>
              <br />
              cup by cup.
            </h1>
            <p className="text-stone-400 text-sm leading-relaxed max-w-sm mb-7">
              House specials, signature pours, one-shop-only creations — we taste and score the drinks you can't recreate at home, so your next standout cup is never a guess.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/search"
                className="inline-flex items-center gap-2 bg-amber-700 text-amber-100 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-amber-600 transition-colors"
              >
                Explore Coffees
              </Link>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 mt-7">
              <div>
                <p className="font-heading text-2xl font-semibold text-white leading-none">
                  {loading ? '—' : allCoffees.length}
                </p>
                <p className="text-[11px] tracking-wide text-stone-400 uppercase mt-1">Coffees</p>
              </div>
              <div className="w-px h-8 bg-stone-700" />
              <div>
                <p className="font-heading text-2xl font-semibold text-white leading-none">
                  {loading ? '—' : reviewCount}
                </p>
                <p className="text-[11px] tracking-wide text-stone-400 uppercase mt-1">Reviews</p>
              </div>
            </div>
          </div>

          {/* Right: hero image */}
          <div className="hidden md:flex items-center justify-end">
            <div className="relative w-80 h-72 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/hero-coffee.jpg"
                alt="Espresso pouring into a pistachio-rimmed signature coffee"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-amber-600/30" />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-8 max-w-5xl mx-auto">
        {/* Today's Pick Up */}
        <TodaysPickUp coffees={allCoffees} loading={loading} />

        {/* Top Rated */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-primary" strokeWidth={2} />
              <h2 className="font-heading font-semibold text-base">Top Rated Coffees</h2>
            </div>
            <Link to="/search" className="text-xs text-primary font-medium hover:underline">
              See all
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4">
              {Array(4).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-56 rounded-2xl" />
              ))}
            </div>
          ) : topCoffees.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {topCoffees.map((coffee, i) => (
                <div key={coffee.id} className={i >= 6 ? 'h-full hidden md:block' : 'h-full'}>
                  <CoffeeCard coffee={coffee} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState message="No coffees yet. Be the first to add one!" />
          )}
        </section>

        {/* Latest Reviews */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} className="text-primary" strokeWidth={2} />
            <h2 className="font-heading font-semibold text-base">Latest Reviews</h2>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              {Array(4).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-2xl" />
              ))}
            </div>
          ) : latestReviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
              {latestReviews.map((review, i) => {
                const coffee = coffeeMap[review.coffee_id];
                return (
                  <Link key={review.id} to={`/coffee/${review.coffee_id}`} className={i >= 5 ? 'group hidden md:block' : 'block group'}>
                    <div className="bg-card rounded-2xl border border-border p-4 space-y-2 hover:opacity-80 transition-opacity">
                      {coffee && (
                        <p className="text-xs font-semibold text-primary">{coffee.name} · {coffee.cafe_name}</p>
                      )}
                      <div className="flex items-center gap-2">
                        <StarRating rating={review.rating} size={13} />
                        <span className="text-xs text-muted-foreground">{review.reviewer_name || 'Anonymous'}</span>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-foreground/80 leading-relaxed line-clamp-3">{review.comment}</p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <EmptyState message="No reviews yet." />
          )}
        </section>

        {/* Badge System Info */}
        <BadgeInfo />

        {/* Top Contributors */}
        <TopContributors />
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-10 text-muted-foreground text-sm">
      <Coffee size={32} className="mx-auto mb-2 opacity-30" strokeWidth={1} />
      <p>{message}</p>
    </div>
  );
}